/* eslint react-hooks/exhaustive-deps: "warn" */
import { type CSSProperties, useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';
import { useIntersection } from 'hooks/useIntersection';

import { Button } from 'ui/atoms/Button/Button';
import { Flag } from 'ui/atoms/Flag/Flag';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './TechnologyHero.module.scss';

type ChainNode = {
  code: string;
  name: string;
  registry: string;
  jurisdiction: string;
  status?: string;
  top?: boolean;
};

type ChainLink = {
  percent: string;
};

type Translate = (key: string) => string;

const getNodes = (t: Translate): ChainNode[] => [
  { code: 'nl', name: 'ACME LOGISTICS SAMPLE BV', registry: 'KVK', jurisdiction: 'Netherlands', status: t('active') },
  { code: 'de', name: 'SAMPLE HOLDING GMBH', registry: 'Handelsregister', jurisdiction: 'Germany' },
  {
    code: 'lu',
    name: 'SAMPLE HOLDINGS SARL',
    registry: 'RCS',
    jurisdiction: 'Luxembourg',
    status: t('ultimate owner'),
    top: true,
  },
];

const LINKS: ChainLink[] = [{ percent: '100%' }, { percent: '61.4%' }];

const NODE_STEP_MS = 260;

const getStats = (t: Translate): { value: number; suffix: string; label: string }[] => [
  { value: 400, suffix: '+', label: t('registries') },
  { value: 200, suffix: '+', label: t('countries') },
  { value: 600, suffix: 'M+', label: t('profiles') },
];

export const TechnologyHero = () => {
  const { t } = useInt();
  const consoleRef = useRef<HTMLDivElement>(null);
  const revealed = useIntersection(consoleRef, { threshold: 0.2 });
  const [playKey, setPlayKey] = useState(0);
  const nodes = getNodes(t);
  const stats = getStats(t);
  const [counts, setCounts] = useState(stats.map(() => 0));

  useEffect(() => {
    if (!revealed) {
      return undefined;
    }

    const steps = 24;
    let step = 0;
    const id = setInterval(() => {
      step += 1;
      const progress = Math.min(step / steps, 1);
      setCounts(stats.map((stat) => Math.round(stat.value * progress)));
      if (progress >= 1) {
        clearInterval(id);
      }
    }, 26);

    return () => clearInterval(id);
  }, [revealed, playKey]);

  return (
    <Section className={styles.root}>
      <div className={styles.grid}>
        <div className={styles.col}>
          <TitleBlock
            size="page"
            align="left"
            title={t('The registry, as an endpoint.')}
            description={t(
              'We read 400+ government registers across 200+ countries and hand you the result over REST, MCP, webhooks or a warehouse feed. Fintechs, marketplaces, SaaS platforms and AI teams build on it.',
            )}
          />

          <div className={styles.actions}>
            <Button type="blue" size="xxl" href="/free-trial">
              {t('Get a test key')}
            </Button>
            <Button type="dark-outline" size="xxl" href="https://api.globaldatabase.com/docs/v2/#setup">
              {t('Read the docs')}
            </Button>
          </div>

          <div className={cn(styles.stats, revealed && styles.statsOn)}>
            {stats.map((stat, i) => (
              <div className={styles.stat} key={stat.label}>
                <b className={styles.statN}>
                  {counts[i]}
                  <em>{stat.suffix}</em>
                </b>
                <span className={styles.statL}>{stat.label}</span>
              </div>
            ))}
            <div className={styles.stat}>
              <b className={styles.statN}>{t('Daily')}</b>
              <span className={styles.statL}>{t('sync')}</span>
            </div>
          </div>
        </div>

        <div className={styles.consoleWrap} ref={consoleRef}>
          <div className={cn(styles.console, revealed && styles.consoleOn)} key={playKey}>
            <div className={styles.consoleBar}>
              <span className={styles.led} />
              <span>
                {t('ownership chain')} <span className={styles.dot}>&middot;</span> <b>{t('3 registers')}</b>
              </span>
              <span className={styles.sample}>SAMPLE</span>
            </div>

            <div className={styles.consoleBody}>
              {nodes.map((node, i) => (
                <div key={node.name}>
                  <div
                    className={cn(styles.node, node.top && styles.nodeTop)}
                    style={{ '--d': `${i * NODE_STEP_MS * 2}ms` } as CSSProperties}
                  >
                    <Flag code={node.code} className={styles.flag} />
                    <span>
                      <span className={styles.nodeName}>{node.name}</span>
                      <span className={styles.nodeReg}>
                        {node.registry} &middot; {node.jurisdiction}
                        {node.status ? ` · ${node.status}` : ''}
                      </span>
                    </span>
                  </div>

                  {LINKS[i] && (
                    <div
                      className={styles.link}
                      style={{ '--d': `${i * NODE_STEP_MS * 2 + NODE_STEP_MS}ms` } as CSSProperties}
                    >
                      <span>
                        {t('held')} <b>{LINKS[i].percent}</b> {t('by')}
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className={styles.consoleFoot}>
              <span>
                {t('3 levels resolved')} &middot; {t('0 unresolved branches')}
              </span>
              <b>{t('61.4% effective')}</b>
            </div>
          </div>

          <button type="button" className={styles.replay} onClick={() => setPlayKey((key) => key + 1)}>
            {t('Replay resolution')}
          </button>
        </div>
      </div>
    </Section>
  );
};
